/**
 * MODULE: domain/sales-intelligence/frontend/components/PipelineStatusPanel.tsx
 * PURPOSE: Debug panel for Sales Intelligence pipeline observability (dev only)
 *
 * DEPENDENCIES:
 *   - react: useState, useEffect
 *   - flowbite-react: Card, Badge, Spinner (optional)
 *
 * PUBLIC API:
 *   - PipelineStatusPanel: Shows DB, RabbitMQ, last activity status when isLoading and (dev or ?debug=true)
 *
 * SIDE EFFECTS: Polls GET /api/v1/health/pipeline every 5s while visible
 *
 * AGENT SAFETY: SAFE
 *
 * Recent: 2026-02-09 - Show database_message when DB error (PipelineHealth.database_message); PipelineHealth interface includes database_message. 2026-02-08 - Health URL targets orchestration gateway (port 8080); Phase 0 observability.
 */

import React, { useState, useEffect } from 'react';

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
const showDebug = () => isDev || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === 'true');

export interface PipelineStatusPanelProps {
  isLoading: boolean;
  companyName?: string;
}

interface PipelineHealth {
  database: 'ok' | 'error';
  database_message?: string | null;
  rabbitmq: 'ok' | 'error' | 'not_configured';
  last_research_activity: {
    id: string;
    created_at: string | null;
    activity_type: string;
    notes: string;
  } | null;
}

import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
// Health endpoint is served via API Gateway (proxy in dev)
const baseURL = getApiBaseUrl();

export function PipelineStatusPanel({ isLoading, companyName }: PipelineStatusPanelProps) {
  const [health, setHealth] = useState<PipelineHealth | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!showDebug() || !isLoading) return;

    const fetchHealth = async () => {
      try {
        const params = companyName ? `?company_name=${encodeURIComponent(companyName)}` : '';
        const res = await fetch(`${baseURL}/api/v1/health/pipeline${params}`);
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        setHealth(data);
        setFetchError(null);
      } catch (e) {
        setFetchError(e instanceof Error ? e.message : 'Failed to fetch');
        setHealth(null);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 5000);
    return () => clearInterval(interval);
  }, [isLoading, companyName]);

  if (!showDebug() || !isLoading) return null;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Pipeline status (debug)</div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1">
          DB:{' '}
          {health?.database === 'ok' ? (
            <span className="text-green-600 dark:text-green-400">✅</span>
          ) : health?.database === 'error' || fetchError ? (
            <span className="text-red-600 dark:text-red-400">❌</span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </span>
        <span className="flex items-center gap-1">
          RabbitMQ:{' '}
          {health?.rabbitmq === 'ok' ? (
            <span className="text-green-600 dark:text-green-400">✅</span>
          ) : health?.rabbitmq === 'error' ? (
            <span className="text-red-600 dark:text-red-400">❌</span>
          ) : health?.rabbitmq === 'not_configured' ? (
            <span className="text-gray-400">⚪</span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </span>
        {health?.last_research_activity && (
          <span className="text-gray-600 dark:text-gray-300">
            Last activity: {health.last_research_activity.activity_type} @ {health.last_research_activity.created_at ?? '—'}
          </span>
        )}
        {(health?.database === 'error' && (health?.database_message || fetchError)) && (
          <span className="block w-full text-xs text-red-600 dark:text-red-400" title={health?.database_message || fetchError || ''}>
            {health.database_message || fetchError}
          </span>
        )}
        {fetchError && <span className="text-red-600 dark:text-red-400">{fetchError}</span>}
      </div>
    </div>
  );
}
